import { SUPPLY_IDS, type SupplyId } from "@frontier/content/supplies";
import type { EquipmentKey } from "@frontier/content/arsenal";
import { z } from "zod";
import { useEffect, useRef, useState } from "react";
import type { SupplyStationView } from "@frontier/protocol/supplies";
import { EquipmentArt } from "./EquipmentArt";
import { ResourceSymbol } from "./ResourceSymbol";
import { credits } from "./ui-labels";
import { sameSupplyContext } from "./supply-intents";
import "./station-pages.css";
import "./supplies.css";

export interface SupplyPanelProps {
  selectedSupplyId?: SupplyId;
  onSelectSupply?: (id: SupplyId) => void;
  onOpenHold?: (id: SupplyId) => void;
  mode?: "all" | "hold" | "shop";
  view: SupplyStationView | null;
  busy: boolean;
  pending: boolean;
  recovery?: boolean;
  onRecover?: () => void;
  onBuy: (supplyId: SupplyId, quantity: number) => Promise<SupplyStationView | void> | void;
  onTransfer: (
    supplyId: SupplyId,
    direction: "load" | "unload",
    quantity: number,
  ) => void;
  onRetry: () => void;
  onReload: () => void;
}
export function SupplyPanel({
  selectedSupplyId,
  onSelectSupply,
  onOpenHold,
  mode = "all",
  view,
  busy,
  pending,
  recovery = false,
  onRecover,
  onBuy,
  onTransfer,
  onRetry,
  onReload,
}: SupplyPanelProps) {
  const [quantity, setQuantity] = useState(5);
  const [localSupplyId, setLocalSupplyId] = useState<SupplyId>("battery");
  const supplyId = selectedSupplyId ?? localSupplyId;
  const [receipt, setReceipt] = useState("");
  const sequence = useRef(0);
  const active = useRef({ view, supplyId });
  active.current = { view, supplyId };
  useEffect(() => {
    sequence.current++;
    setReceipt("");
    return () => { sequence.current++; };
  }, [view?.runId, view?.hullId, view?.hullRevision, supplyId]);
  const buy = async () => {
    if (!view) return;
    const action = ++sequence.current, before = view, id = supplyId, count = quantity;
    const name = view.items.find(item => item.supplyId === id)?.name;
    setReceipt("");
    let confirmed: SupplyStationView | void;
    try { confirmed = await onBuy(id, count); }
    catch (error) {
      if (action === sequence.current && active.current.supplyId === id && active.current.view && sameSupplyContext(before, active.current.view))
        setReceipt(error instanceof Error ? error.message : "Покупка не подтверждена");
      return;
    }
    const current = active.current;
    if (!confirmed || !current.view || !name || action !== sequence.current || current.supplyId !== id ||
      !sameSupplyContext(before, current.view) || !sameSupplyContext(before, confirmed) ||
      (confirmed.items.find(item => item.supplyId === id)?.stock ?? 0) < (before.items.find(item => item.supplyId === id)?.stock ?? 0) + count) return;
    setReceipt(`${name} ×${count} — на складе`);
  };
  const selectSupply = (id: SupplyId) => { setLocalSupplyId(id); onSelectSupply?.(id); };
  const recoveryAction = recovery && <div className="supply-recovery">
    <span>Осталась неподтверждённая операция с припасами.</span>
    <button className="station-button station-button-gold" disabled={busy} onClick={onRecover}>Проверить запас и продолжить</button>
  </div>;
  if (!view)
    return (
      <section className="station-page supply-panel">
        <h2>Припасы</h2>
        {recoveryAction}
        <button className="station-button" onClick={onReload}>
          Загрузить запасы
        </button>
        {pending && (
          <button className="station-button" disabled={busy} onClick={onRetry}>
            Проверить операцию
          </button>
        )}
      </section>
    );
  const item = view.items.find((entry) => entry.supplyId === supplyId);
  if (!item) return null;
  const terms = item.terms;
  const art: EquipmentKey =
    item.kind === "rocket"
      ? { kind: "weapon", id: "missile" }
      : {
          kind: "passive",
          id: item.kind === "drone" ? "repair-drone" : "capacitor",
        };
  if (mode === "shop") return (
    <section className="station-page supply-panel supply-market" aria-label="Магазин припасов">
      <div className="supply-market-content">
      <div className="supply-market-grid" aria-label="Выбор припаса">
        {view.items.map((entry) => {
          const entryArt: EquipmentKey = entry.kind === "rocket"
            ? { kind: "weapon", id: "missile" }
            : { kind: "passive", id: entry.kind === "drone" ? "repair-drone" : "capacitor" };
          return <button key={entry.supplyId} type="button" className="supply-market-tile"
            aria-label={`${entry.name} НА СКЛАДЕ: ${entry.stock} В ТРЮМЕ: ${entry.loaded}`}
            aria-pressed={entry.supplyId === supplyId} onClick={() => selectSupply(entry.supplyId)}>
            <span className="supply-market-art"><EquipmentArt equipment={entryArt} /></span>
            <b>{entry.name}</b><small><span>НА СКЛАДЕ: {entry.stock}</span><span>В ТРЮМЕ: {entry.loaded}</span></small>
          </button>;
        })}
      </div>
      <article className="supply-market-detail">
        <header className="supply-item-preview"><span className="supply-art"><EquipmentArt equipment={art} /></span>
          <div><small>РАСХОДНЫЙ ПРИПАС</small><strong>{item.name}</strong>
            <p className="supply-effect">{terms.algorithm === "battery-v1" ? `+${terms.energy} энергии` : terms.algorithm === "rocket-v1" ? `${terms.damage} урона · требуется ракетница` : `${terms.healingPerSecond} корпуса/с · ${terms.duration} с`} · {terms.cooldownSeconds} с перезарядки</p>
          </div></header>
        <div className="supply-stock-cards"><div><span>НА СКЛАДЕ</span><b>{item.stock}</b></div><div><span>В ТРЮМЕ</span><b>{item.loaded}</b></div></div>
        <p className="supply-market-destination">Покупка поступает на склад станции. Для применения в полёте загрузи припасы в трюм.</p>
        {onOpenHold && <button className="station-button" disabled={busy || pending || recovery} onClick={() => onOpenHold(supplyId)}>Загрузить в трюм →</button>}
      </article>
      </div>
      {(receipt || recovery || pending || busy) && <div className="supply-market-notices">
        {receipt && <p className="purchase-receipt" role="status">{receipt}</p>}
        {recoveryAction}
        {pending && <div className="supply-recovery"><span>Результат операции неизвестен.</span><button className="station-button station-button-gold" disabled={busy} onClick={onRetry}>Проверить операцию</button></div>}
        {(busy || pending || recovery) && <p role="status">{recovery ? "Проверь предыдущую операцию." : pending ? "Проверь результат предыдущей операции." : "Операция выполняется…"}</p>}
      </div>}
      <div className="supply-market-actions market-purchase-bar">
        <label className="supply-market-quantity">Количество
          <select aria-label="Количество припасов" disabled={busy || pending || recovery} value={quantity} onChange={(event) => setQuantity(Number(event.target.value))}>
            {[1, 5, 10, 20].map(value => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        <div className="supply-purchase"><div><span>ПОКУПКА · {quantity} ШТ.</span><b>{credits((BigInt(terms.unitCost.credits) * BigInt(quantity)).toString())} КР <small>+ {(terms.unitCost.components * quantity).toLocaleString("ru-RU")} комп.</small></b></div>
          <button className="station-button station-button-gold" disabled={busy || pending || recovery} onClick={() => { void buy(); }}>Купить на склад</button>
        </div>
      </div>
    </section>
  );
  return (
    <section className="station-page supply-panel" aria-label="Припасы">
      <header className="station-page-heading">
        <div>
          <span className="station-page-kicker">ПОДГОТОВКА К ВЫЛЕТУ</span>
          <h2>Припасы</h2>
          <p>
            5 одинаковых припасов — 1 ячейка. При гибели выпадает всё
            загруженное.
          </p>
        </div>
        <div className="supply-hold">
          <span>ЗАНЯТО В ТРЮМЕ</span>
          <b>
            {view.hold.usedSlots}
            <small> / {view.hold.capacity}</small>
          </b>
          <progress
            aria-label="Занятые ячейки трюма"
            value={view.hold.usedSlots}
            max={view.hold.capacity}
          />
        </div>
      </header>
      <div className="supply-layout">
        <article className="supply-item">
          <label className="supply-picker">
            ПРИПАС
            <select
              aria-label="Тип припаса"
              value={supplyId}
              disabled={busy || pending || recovery}
              onChange={(event) =>
                selectSupply(z.enum(SUPPLY_IDS).parse(event.target.value))
              }
            >
              {view.items.map((entry) => (
                <option key={entry.supplyId} value={entry.supplyId}>
                  {entry.name}
                </option>
              ))}
            </select>
          </label>
          <div className="supply-item-preview">
            <span className="supply-art">
              <EquipmentArt equipment={art} />
            </span>
            <div>
              <small>РАСХОДНЫЙ ПРИПАС</small>
              <strong>{item.name}</strong>
            </div>
          </div>
          <p className="supply-effect">
            {terms.algorithm === "battery-v1"
              ? `+${terms.energy} энергии`
              : terms.algorithm === "rocket-v1"
                ? `${terms.damage} урона · требуется ракетница`
                : `${terms.healingPerSecond} корпуса/с · ${terms.duration} с`}
          </p>
          <span className="supply-cooldown">
            Перезарядка {terms.cooldownSeconds} с
          </span>
          <div className="supply-unit-price">
            <span>СТОИМОСТЬ / ШТ.</span>
            <b>
              <ResourceSymbol kind="credits" />
              {credits(terms.unitCost.credits)} КР
            </b>
            <small>
              + {terms.unitCost.components.toLocaleString("ru-RU")} комп.
            </small>
          </div>
        </article>
        <section className="supply-logistics" aria-label="Склад и трюм">
          <div className="supply-logistics-heading">
            <div>
              <span className="station-page-kicker">СКЛАД → КОРАБЛЬ</span>
              <h3>Подготовь запас</h3>
            </div>
            <label>
              Количество
              <select
                aria-label="Количество припасов"
                disabled={busy || pending || recovery}
                value={quantity}
                onChange={(event) => setQuantity(Number(event.target.value))}
              >
                {[1, 5, 10, 20].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="supply-stock-cards">
            <div>
              <span>НА СКЛАДЕ</span>
              <b>{item.stock}</b>
            </div>
            <div>
              <span>В ТРЮМЕ</span>
              <b>{item.loaded}</b>
            </div>
          </div>
          <div className="supply-actions">
            <button
              className="station-button station-button-primary"
              disabled={busy || pending || recovery || item.stock < quantity}
              onClick={() => onTransfer(supplyId, "load", quantity)}
            >
              В трюм ↓
            </button>
            <button
              className="station-button"
              disabled={busy || pending || recovery || item.loaded < quantity}
              onClick={() => onTransfer(supplyId, "unload", quantity)}
            >
              На склад ↑
            </button>
          </div>
          {mode !== "hold" && <div className="supply-purchase">
            <div>
              <span>ПОКУПКА · {quantity} ШТ.</span>
              <b>
                {credits(
                  (
                    BigInt(terms.unitCost.credits) * BigInt(quantity)
                  ).toString(),
                )}{" "}
                КР{" "}
                <small>
                  +{" "}
                  {(terms.unitCost.components * quantity).toLocaleString(
                    "ru-RU",
                  )}{" "}
                  комп.
                </small>
              </b>
            </div>
            <button
              className="station-button station-button-gold"
              disabled={busy || pending || recovery}
              onClick={() => onBuy(supplyId, quantity)}
            >
              Купить на склад
            </button>
          </div>}
        </section>
      </div>
      {recoveryAction}
      {pending && (
        <div className="supply-recovery">
          <span>Результат операции неизвестен.</span>
          <button
            className="station-button station-button-gold"
            disabled={busy}
            onClick={onRetry}
          >
            Проверить операцию
          </button>
        </div>
      )}
    </section>
  );
}
